import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';

const LANGUAGES = [
  // Americas
  { code: 'en', flag: '🇺🇸', name: 'English', nativeName: 'English', region: 'Americas' },
  { code: 'pt', flag: '🇧🇷', name: 'Portuguese', nativeName: 'Português', region: 'Americas' },
  { code: 'es', flag: '🇪🇸', name: 'Spanish', nativeName: 'Español', region: 'Americas' },
  // Europe
  { code: 'fr', flag: '🇫🇷', name: 'French', nativeName: 'Français', region: 'Europe' },
  { code: 'de', flag: '🇩🇪', name: 'German', nativeName: 'Deutsch', region: 'Europe' },
  { code: 'it', flag: '🇮🇹', name: 'Italian', nativeName: 'Italiano', region: 'Europe' },
  // Asia
  { code: 'ja', flag: '🇯🇵', name: 'Japanese', nativeName: '日本語', region: 'Asia' },
  { code: 'zh', flag: '🇨🇳', name: 'Chinese', nativeName: '中文', region: 'Asia' },
  { code: 'ko', flag: '🇰🇷', name: 'Korean', nativeName: '한국어', region: 'Asia' },
];

const REGIONS = ['Americas', 'Europe', 'Asia'];

export default function LanguageSelector() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('language_selector.select_language')}
        className="flex items-center gap-1.5 px-2 py-1.5 text-zinc-400 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm"
      >
        <span className="text-base">{currentLang.flag}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('language_selector.language_options')}
          className="absolute right-0 top-full mt-2 w-56 bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl z-[100] overflow-hidden"
        >
          {REGIONS.map(region => (
            <div key={region}>
              <div className="px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-900/50">
                {region}
              </div>
              {LANGUAGES.filter(l => l.region === region).map(lang => (
                <button
                  key={lang.code}
                  role="option"
                  aria-selected={lang.code === currentLang.code}
                  onClick={() => handleSelect(lang.code)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                    lang.code === currentLang.code
                      ? 'bg-amber-500/10 text-amber-500'
                      : 'text-zinc-300 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <span className="text-base" aria-hidden="true">{lang.flag}</span>
                  <div className="flex flex-col flex-1 text-left">
                    <span className="text-sm">{lang.nativeName}</span>
                    <span className="text-[10px] text-zinc-500 font-mono">{lang.code.toUpperCase()}</span>
                  </div>
                  {lang.code === currentLang.code && (
                    <Check className="w-4 h-4 text-amber-500" />
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
